/**
 * Licences screen shared pieces (#1130, slice `web-licences-screen`): queries, the colour bands, the one legend,
 * banded values with formula tooltips, and the grouped sortable table every tab uses.
 *
 * Reads go through the generated SDK + `unwrap()`, so a failed lookup throws and is never shown as an empty list
 * (hard rule 10). Money and cost are absent for a role that may not see them (hard rule 5): absent renders "—",
 * never $0.00, and a band built on an absent figure is not drawn.
 */
import { cn, Tooltip } from '@patchbay/ui'
import { queryOptions } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import * as React from 'react'
import { unwrap } from '../../lib/api/client'
import {
  type BooksFixOut,
  type LicenceChangesOut,
  type LicenceMappingOut,
  type LicenceNoteOut,
  type LicenceReconOut,
  type LicenceUsageOut,
  licenceBooksFixApiV1LicencesBooksFixGet,
  licenceMappingApiV1LicencesMappingGet,
  licenceReconApiV1LicencesReconGet,
  licenceUsageApiV1LicencesUsageGet,
  listLicenceChangesApiV1LicencesChangesGet,
  listLicenceNotesApiV1LicencesNotesGet,
  type MarginBand,
  type ReconLineOut,
} from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { DASH, type MoneyValue, toNumber } from '../profit/format'

// --- roles and queries -------------------------------------------------------------------------------------------------

/** The API's BillingActorDep: manager and billing. Cover and tech never open the screen. */
export const LICENCE_ROLES = new Set(['manager', 'billing'])
export const canSeeLicences = (role: string | null | undefined): boolean => Boolean(role && LICENCE_ROLES.has(role))

export const licenceKeys = {
  all: ['licences'] as const,
  recon: ['licences', 'recon'] as const,
  usage: ['licences', 'usage'] as const,
  booksFix: ['licences', 'books-fix'] as const,
  changes: ['licences', 'changes'] as const,
  mapping: ['licences', 'mapping'] as const,
  notes: (lineKey: string) => ['licences', 'notes', lineKey] as const,
}

export const reconQuery = () =>
  queryOptions({ queryKey: licenceKeys.recon, queryFn: () => unwrap<LicenceReconOut>(licenceReconApiV1LicencesReconGet()) })
export const usageQuery = () =>
  queryOptions({ queryKey: licenceKeys.usage, queryFn: () => unwrap<LicenceUsageOut>(licenceUsageApiV1LicencesUsageGet()) })
export const booksFixQuery = () =>
  queryOptions({ queryKey: licenceKeys.booksFix, queryFn: () => unwrap<BooksFixOut>(licenceBooksFixApiV1LicencesBooksFixGet()) })
export const changesQuery = () =>
  queryOptions({
    queryKey: licenceKeys.changes,
    queryFn: () => unwrap<LicenceChangesOut>(listLicenceChangesApiV1LicencesChangesGet({ query: { limit: 200 } })),
  })
export const mappingQuery = () =>
  queryOptions({ queryKey: licenceKeys.mapping, queryFn: () => unwrap<LicenceMappingOut>(licenceMappingApiV1LicencesMappingGet()) })
export const notesQuery = (lineKey: string) =>
  queryOptions({
    queryKey: licenceKeys.notes(lineKey),
    queryFn: () => unwrap<LicenceNoteOut[]>(listLicenceNotesApiV1LicencesNotesGet({ query: { line_key: lineKey } })),
  })

// --- formatting --------------------------------------------------------------------------------------------------------

const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' })
const COMPACT = new Intl.NumberFormat('en-CA', { notation: 'compact', maximumFractionDigits: 1 })

export const num = toNumber
/** A withheld or not-applicable figure. */
export const DASH_CELL = <span className="text-muted-foreground">{DASH}</span>
export const cad = (value: MoneyValue): string => {
  const n = toNumber(value)
  return n === null ? DASH : CAD.format(n)
}
export const cadCompact = (value: number | null): string => (value === null ? DASH : `$${COMPACT.format(value)}`)
export const qty = (value: MoneyValue): string => {
  const n = toNumber(value)
  return n === null ? DASH : String(+n.toFixed(2))
}
export const pct1 = (value: MoneyValue): string => {
  const n = toNumber(value)
  return n === null ? DASH : `${n.toFixed(1)}%`
}
/** "+$1.20" / "−$1.20" (a real minus sign so a column of them lines up). */
export const signed = (value: number, fmt: (abs: number) => string): string => `${value >= 0 ? '+' : '−'}${fmt(Math.abs(value))}`

export function dayLabel(value: string | null | undefined): string {
  if (!value) return DASH
  const d = new Date(`${value.slice(0, 10)}T12:00:00`)
  return Number.isNaN(d.valueOf()) ? value : formatWhen(d.toISOString(), { style: 'date' })
}

export function daysFrom(value: string | null | undefined, now: Date): number | null {
  if (!value) return null
  const t = Date.parse(value.length === 10 ? `${value}T12:00:00` : value)
  return Number.isNaN(t) ? null : Math.round((t - now.getTime()) / 86_400_000)
}

/** Billing and commitment term as one line ("Monthly · 1-Year"). */
export const termLabel = (line: Pick<ReconLineOut, 'billing_term' | 'commitment_term'>): string =>
  [line.billing_term, line.commitment_term].filter(Boolean).join(' · ')

export const customerLabel = (line: Pick<ReconLineOut, 'company_name' | 'books_customer_name' | 'pax8_company_id'>): string =>
  line.company_name ?? line.books_customer_name ?? (line.pax8_company_id ? `Pax8 company ${line.pax8_company_id}` : 'Unknown company')

/** The Pax8 portal page for a company (P-X6, answered 2026-10-30). The API's own link wins when it has one. */
export function pax8Url(companyId: string | null | undefined, apiLink?: string | null): string | null {
  if (apiLink) return apiLink
  return companyId ? `https://app.pax8.com/account/clients/${encodeURIComponent(companyId)}?activeTab=subscriptions` : null
}

// --- bands -------------------------------------------------------------------------------------------------------------

/** Margin bands (#1123): negative is the top flag, then under 5 / 10 / 20, then OK. `unknown` is never OK. */
export const MARGIN_EDGES = [0, 5, 10, 20] as const

export function marginBand(marginPct: MoneyValue): MarginBand {
  const m = toNumber(marginPct)
  if (m === null) return 'unknown'
  if (m < MARGIN_EDGES[0]) return 'negative'
  if (m < MARGIN_EDGES[1]) return 'under_5'
  if (m < MARGIN_EDGES[2]) return 'under_10'
  if (m < MARGIN_EDGES[3]) return 'under_20'
  return 'ok'
}

/** The server's band wins (it applies the configured edges); the local one only fills a missing band. */
export const bandOf = (server: MarginBand | null | undefined, marginPct: MoneyValue): MarginBand =>
  server && server !== 'unknown' ? server : marginBand(marginPct)

export const MARGIN_BANDS: { band: Exclude<MarginBand, 'unknown'>; label: string }[] = [
  { band: 'negative', label: 'Negative' },
  { band: 'under_5', label: 'Under 5%' },
  { band: 'under_10', label: 'Under 10%' },
  { band: 'under_20', label: 'Under 20%' },
  { band: 'ok', label: '20% or more' },
]

/** ± MSRP bands (#1150): amber more than 5% under MSRP, red more than 15% under. */
export type MsrpBand = 'at_or_above' | 'within_5' | 'under_5' | 'under_15'
export const MSRP_NEAR_PCT = 5
export const MSRP_FAR_PCT = 15

export function msrpBand(deltaPct: MoneyValue): MsrpBand | null {
  const d = toNumber(deltaPct)
  if (d === null) return null
  if (d >= 0) return 'at_or_above'
  if (d >= -MSRP_NEAR_PCT) return 'within_5'
  if (d >= -MSRP_FAR_PCT) return 'under_5'
  return 'under_15'
}

export const MSRP_BANDS: { band: MsrpBand; label: string }[] = [
  { band: 'at_or_above', label: 'At or above MSRP' },
  { band: 'within_5', label: 'Up to 5% under' },
  { band: 'under_5', label: '5–15% under' },
  { band: 'under_15', label: 'Over 15% under' },
]

/** `(we bill − MSRP) / MSRP`, signed percent. The server's `msrp_delta_pct` wins when present. */
export function msrpDelta(server: MoneyValue, billed: MoneyValue, msrp: MoneyValue): number | null {
  const s = toNumber(server)
  if (s !== null) return s
  const b = toNumber(billed)
  const m = toNumber(msrp)
  return b === null || m === null || m === 0 ? null : ((b - m) / m) * 100
}

/** `(we bill − cost) / we bill`, percent: margin on revenue. */
export function marginPct(billed: MoneyValue, cost: MoneyValue): number | null {
  const b = toNumber(billed)
  const c = toNumber(cost)
  return b === null || c === null || b === 0 ? null : ((b - c) / b) * 100
}

/**
 * One palette for both sets, from the kit's tokens, light and dark. Under 10% is the outlined amber so it reads apart
 * from the filled amber of under 5% without a new colour.
 */
export const TONE = {
  bad: 'bg-destructive-soft text-destructive',
  warn: 'bg-warning-soft text-warning',
  caution: 'bg-transparent text-warning ring-1 ring-inset ring-warning/45',
  quiet: 'bg-muted text-foreground',
  ok: 'bg-success-soft text-success',
} as const

export const MARGIN_TONE: Record<MarginBand, string> = {
  negative: TONE.bad,
  under_5: TONE.warn,
  under_10: TONE.caution,
  under_20: TONE.quiet,
  ok: TONE.ok,
  unknown: 'text-muted-foreground',
}

export const MSRP_TONE: Record<MsrpBand, string> = {
  at_or_above: TONE.ok,
  within_5: TONE.quiet,
  under_5: TONE.warn,
  under_15: TONE.bad,
}

/** Swimlane dot fill per margin band. */
export const DOT_TONE: Record<MarginBand, string> = {
  negative: 'bg-destructive',
  under_5: 'bg-warning',
  under_10: 'bg-warning/55',
  under_20: 'bg-muted-foreground',
  ok: 'bg-success',
  unknown: 'bg-border-strong',
}

export const BAND_RANK: Record<MarginBand, number> = { negative: 0, under_5: 1, under_10: 2, under_20: 3, unknown: 4, ok: 5 }

export const marginBandLabel = (band: MarginBand): string =>
  band === 'unknown' ? 'Unknown: a side of the comparison is missing' : (MARGIN_BANDS.find((b) => b.band === band)?.label ?? band)

// --- tooltips ----------------------------------------------------------------------------------------------------------

/** Tooltip lines for a margin value: the band, the formula and the numbers it came from. */
export function marginTip(opts: {
  when: string
  billed: MoneyValue
  cost: MoneyValue
  margin: MoneyValue
  band: MarginBand
  seats?: MoneyValue
  unit?: string
}): string[] {
  const b = toNumber(opts.billed)
  const c = toNumber(opts.cost)
  const lines = [marginBandLabel(opts.band)]
  if (b === null || c === null) return [...lines, `${opts.when}: we bill ${cad(opts.billed)}, cost ${cad(opts.cost)}`]
  const unit = opts.unit ? ` ${opts.unit}` : ''
  lines.push(`${opts.when}: we bill ${cad(b)}, cost ${cad(c)}${unit}`)
  lines.push(`Margin = (we bill − cost) ÷ we bill = ${signed(b - c, cad)} ÷ ${cad(b)} = ${pct1(opts.margin)}`)
  const seats = toNumber(opts.seats)
  if (seats !== null && seats > 0) lines.push(`${signed((b - c) * seats, cad)} a month on ${qty(seats)} seats`)
  return lines
}

/** Tooltip lines for a ± MSRP value. */
export function msrpTip(opts: { billed: MoneyValue; msrp: MoneyValue; delta: number; unit?: string }): string[] {
  const band = msrpBand(opts.delta)
  const b = toNumber(opts.billed)
  const m = toNumber(opts.msrp)
  const lines = [MSRP_BANDS.find((x) => x.band === band)?.label ?? '']
  if (b !== null && m !== null) {
    lines.push(`We bill ${cad(b)}, MSRP ${cad(m)}${opts.unit ? ` ${opts.unit}` : ''}`)
    lines.push(`± MSRP = (we bill − MSRP) ÷ MSRP = ${signed(b - m, cad)} ÷ ${cad(m)} = ${signed(opts.delta, (x) => `${x.toFixed(1)}%`)}`)
  }
  return lines
}

/**
 * A coloured value with its detail in a tooltip (hover or keyboard focus). The detail is also the accessible name, so
 * a screen reader and a test read the same formula a mouse user sees.
 */
export function BandValue({ text, tone, tip, testId }: { text: string; tone: string; tip: string[]; testId?: string }) {
  const label = `${text}. ${tip.filter(Boolean).join('. ')}`
  return (
    <Tooltip
      content={
        <span className="block max-w-80 space-y-0.5 py-0.5 text-left">
          {tip.filter(Boolean).map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </span>
      }
    >
      <button
        type="button"
        aria-label={label}
        data-testid={testId}
        className={cn('tabular inline-block cursor-help rounded-sm px-1.5 py-px font-mono text-xs leading-5', tone)}
      >
        {text}
      </button>
    </Tooltip>
  )
}

export function MarginValue(props: {
  when: string
  billed: MoneyValue
  cost: MoneyValue
  margin: MoneyValue
  band?: MarginBand | null
  seats?: MoneyValue
  unit?: string
}) {
  const m = toNumber(props.margin) ?? marginPct(props.billed, props.cost)
  if (m === null) return <span className="text-muted-foreground">{DASH}</span>
  const band = bandOf(props.band, m)
  return <BandValue text={pct1(m)} tone={MARGIN_TONE[band]} tip={marginTip({ ...props, margin: m, band })} testId={`margin-${band}`} />
}

export function MsrpValue({ server, billed, msrp, unit }: { server: MoneyValue; billed: MoneyValue; msrp: MoneyValue; unit?: string }) {
  const d = msrpDelta(server, billed, msrp)
  if (d === null) return <span className="text-muted-foreground">{DASH}</span>
  const band = msrpBand(d) as MsrpBand
  return (
    <BandValue
      text={signed(d, (x) => `${x.toFixed(1)}%`)}
      tone={MSRP_TONE[band]}
      tip={msrpTip({ billed, msrp, delta: d, unit })}
      testId={`msrp-${band}`}
    />
  )
}

/** The one legend: margin bands, ± MSRP bands and the quantity/billing marks, read once for the whole table. */
export function Legend({ msrp = true, quantity = true }: { msrp?: boolean; quantity?: boolean }) {
  const swatch = (tone: string, label: string) => (
    <span key={label} className={cn('inline-block rounded-sm px-1.5 py-px text-2xs font-medium leading-5', tone)}>
      {label}
    </span>
  )
  return (
    <ul aria-label="Legend" className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
      <li className="flex flex-wrap items-center gap-1.5">
        <span className="mr-0.5">Margin</span>
        {MARGIN_BANDS.map((b) => swatch(MARGIN_TONE[b.band], b.label))}
      </li>
      {msrp ? (
        <li className="flex flex-wrap items-center gap-1.5">
          <span className="mr-0.5">± MSRP</span>
          {MSRP_BANDS.map((b) => swatch(MSRP_TONE[b.band], b.label))}
        </li>
      ) : null}
      {quantity ? (
        <li className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono font-medium text-destructive">29</span>
          <span>Books differs from Pax8</span>
        </li>
      ) : null}
      <li>Hover or tab to a number for the formula.</li>
    </ul>
  )
}

// --- small pieces ------------------------------------------------------------------------------------------------------

export function ExtLink({
  href,
  children,
  className,
  label,
}: {
  href?: string | null
  children: React.ReactNode
  className?: string
  label?: string
}) {
  if (!href) return <span className={className}>{children}</span>
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={cn(
        'underline decoration-border-strong underline-offset-2 hover:text-primary hover:decoration-primary focus-visible:outline-2 focus-visible:outline-ring',
        className,
      )}
    >
      {children}
    </a>
  )
}

export function LinkButton({ href, children }: { href?: string | null; children: React.ReactNode }) {
  if (!href) return null
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium hover:bg-accent [&_svg]:size-3"
    >
      {children}
      <ExternalLink aria-hidden />
    </a>
  )
}

/** Filter chips with counts (the prototype's quick filters). */
export function FilterChips<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: { id: T; label: string; count?: number }[]
  onChange: (id: T) => void
}) {
  return (
    <fieldset aria-label={label} className="m-0 flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors',
            value === o.id ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-foreground hover:bg-accent',
          )}
        >
          {o.label}
          {o.count !== undefined ? <span className="tabular font-mono opacity-70">{o.count}</span> : null}
        </button>
      ))}
    </fieldset>
  )
}

export function SearchBox({
  value,
  onChange,
  label,
  count,
}: {
  value: string
  onChange: (v: string) => void
  label: string
  count?: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="search"
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        className="h-8 w-full max-w-sm rounded-md border border-input bg-background px-2.5 text-sm placeholder:text-subtle focus-visible:outline-2 focus-visible:outline-ring"
      />
      {count ? <span className="text-xs text-muted-foreground">{count}</span> : null}
    </div>
  )
}

// --- grouped table -----------------------------------------------------------------------------------------------------

export interface GroupedColumn<T> {
  id: string
  header: string
  /** Consecutive columns with the same group share one group header; '' or ' ' is an unlabelled group (' ' still gets its rule). */
  group: string
  cell: (row: T) => React.ReactNode
  sortValue?: (row: T) => string | number | null
  numeric?: boolean
  /** Hidden on narrow screens. */
  narrow?: boolean
  className?: string
}

type Sort = { id: string; dir: 1 | -1 }

/** Mark the first column of each group (left rule) and shade every other labelled group, like a ledger. */
function groupMarks<T>(cols: GroupedColumn<T>[]): string[] {
  let prev: string | symbol = Symbol('start')
  let n = -1
  return cols.map((c) => {
    const start = c.group !== prev
    if (start) {
      n++
      prev = c.group
    }
    return cn(start && n > 0 && 'border-l border-border', c.group && n % 2 === 1 && 'bg-muted/40')
  })
}

export function sortRows<T>(rows: T[], col: GroupedColumn<T> | undefined, dir: 1 | -1): T[] {
  if (!col?.sortValue) return rows
  const key = col.sortValue
  return [...rows].sort((a, b) => {
    const av = key(a)
    const bv = key(b)
    // A missing value sorts last whichever way the column runs: absent is not small.
    if (av === null && bv === null) return 0
    if (av === null) return 1
    if (bv === null) return -1
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
    return String(av).localeCompare(String(bv)) * dir
  })
}

export function GroupedTable<T>({
  label,
  columns,
  rows,
  rowKey,
  initialSort,
  empty,
  rowProps,
  renderBefore,
}: {
  label: string
  columns: GroupedColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  initialSort?: Sort
  empty: React.ReactNode
  rowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement> & Record<`data-${string}`, string>
  /** A full-width row before this one (the Renewals month header). */
  renderBefore?: (row: T, index: number, sorted: T[], sort: Sort | null) => React.ReactNode
}) {
  const [sort, setSort] = React.useState<Sort | null>(initialSort ?? null)
  const marks = groupMarks(columns)
  const sorted = React.useMemo(
    () =>
      sort
        ? sortRows(
            rows,
            columns.find((c) => c.id === sort.id),
            sort.dir,
          )
        : rows,
    [rows, columns, sort],
  )
  const groups: { label: string; span: number; mark: string; narrow: boolean }[] = []
  columns.forEach((c, i) => {
    const last = groups[groups.length - 1]
    if (i > 0 && columns[i - 1]?.group === c.group && last) {
      last.span++
      last.narrow = last.narrow && Boolean(c.narrow)
    } else groups.push({ label: c.group, span: 1, mark: marks[i] ?? '', narrow: Boolean(c.narrow) })
  })
  return (
    <div className="max-h-[72vh] overflow-auto rounded-lg border border-border bg-card">
      <table aria-label={label} className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-card">
          <tr>
            {groups.map((g, i) => (
              <th
                // biome-ignore lint/suspicious/noArrayIndexKey: group headers are positional.
                key={i}
                colSpan={g.span}
                scope="colgroup"
                className={cn(
                  'h-6 px-2 pt-1.5 text-center text-2xs font-semibold tracking-wider uppercase',
                  g.label.trim() ? 'text-primary' : '',
                  g.mark,
                  g.narrow && 'max-md:hidden',
                )}
              >
                {g.label.trim() ? <span className="block border-b-2 border-primary/60 pb-0.5">{g.label}</span> : null}
              </th>
            ))}
          </tr>
          <tr className="border-b border-border">
            {columns.map((c, i) => {
              const active = sort?.id === c.id
              return (
                <th
                  key={c.id}
                  scope="col"
                  aria-sort={active ? (sort?.dir === 1 ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    'px-2 py-1.5 text-xs font-semibold whitespace-nowrap text-muted-foreground',
                    c.numeric ? 'text-right' : 'text-left',
                    marks[i],
                    c.narrow && 'max-md:hidden',
                  )}
                >
                  {c.sortValue ? (
                    <button
                      type="button"
                      className="cursor-pointer hover:text-foreground"
                      onClick={() => setSort(active && sort ? { id: c.id, dir: sort.dir === 1 ? -1 : 1 } : { id: c.id, dir: 1 })}
                    >
                      {c.header}
                      {active ? (sort?.dir === 1 ? ' ↑' : ' ↓') : ''}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="p-6">
                {empty}
              </td>
            </tr>
          ) : (
            sorted.map((row, index) => (
              <React.Fragment key={rowKey(row)}>
                {renderBefore?.(row, index, sorted, sort)}
                <tr
                  {...rowProps?.(row)}
                  className={cn('border-b border-border last:border-0 hover:bg-accent/40', rowProps?.(row)?.className)}
                >
                  {columns.map((c, i) => (
                    <td
                      key={c.id}
                      className={cn(
                        'px-2 py-1.5 align-top',
                        c.numeric && 'text-right whitespace-nowrap tabular font-mono text-xs',
                        marks[i],
                        c.narrow && 'max-md:hidden',
                        c.className,
                      )}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
